/* eslint-disable no-unused-vars */
import { useOnClickOutside } from 'hooks';
import React, { useEffect } from 'react';
import Text from 'ui/components/styled/Text';
import { ClearIcon, Container, TextInput } from '../../styled';
import {
  DropdownContainer, DropDownItem, RelativeContainer,
  SearchIcon,
  TagContainer,
  TagsContainer
} from './styles';

const operators = [
  { value: '=', label: 'Equal to' },
  {
    value: '!=',
    label: 'Not equal to',
  },
];
const criterias = [
  { value: 'customer', label: 'Customer' },
  { label: 'Monitor', value: 'job' },
];

const DropDown = React.forwardRef(function Render({ options, onSelect, name, index }, ref) {
  return (
    <DropdownContainer>
      {options.map((option) => (
        <DropDownItem
          onClick={onSelect}
          data-selected={option.value}
          data-name={name}
          data-index={index}
          px="xs"
          py="xs"
          key={option.value}
        >
          {option.label}
        </DropDownItem>
      ))}
    </DropdownContainer>
  );
});

const Tag = ({ filter, index, handleClearFilterItem }) => {
  const { criteria, value, operator } = filter;
  const isCompleted = criteria && value && operator;
  return (
    <TagContainer px="xs" pr="s" isCompleted={isCompleted}>
      <Text fontSize="11px">{criterias.find((one) => one.value === criteria)?.label}</Text>
      <Text fontSize="11px">{operator}</Text>
      <Text fontSize="11px">{value}</Text>
      <Container
        position="absolute"
        right="-11px"
        top="-11px"
        width="max-content"
        height="max-content"
      >
        <ClearIcon onClick={() => handleClearFilterItem(index)} />
      </Container>
    </TagContainer>
  );
};

export default function AdvancedSearch({ onFilter }) {
  const [value, setValue] = React.useState('');
  const [filters, setFilters] = React.useState([]);
  const [step, setStep] = React.useState(0);
  const [current, setCurrent] = React.useState(0);
  const animate = React.useRef(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const searchRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const hideDropdown = () => setIsOpen(false);

  useOnClickOutside(searchRef, hideDropdown);

  const handleChange = ({ currentTarget: { value } }) => {
    setValue(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && step === 3) {
      setFilters((filters) => {
        const newFilters = [...filters];
        newFilters[current].value = value;
        return newFilters;
      });
      setCurrent(current + 1);
      setStep(1);
      setValue('');
    }
  };

  const isFilterCompleted = (index) =>
    !!filters[index]?.value && !!filters[index]?.operator && !!filters[index]?.criteria;

  const handleClear = () => {
    setValue('');
    setFilters([]);
    setCurrent(0);
    setStep(0);
  };

  const handleClearFilterItem = (index) => {
    setFilters((filters) => filters.filter((_, i) => i !== index));
    isFilterCompleted(index) && setCurrent(current - 1);
    !isFilterCompleted(index) && setStep(0);
  };

  const handleFocus = () => {
    setIsOpen(true);
    if (step === 0) setStep(1);
  };

  const handleSelect = ({
    currentTarget: {
      dataset: { name, selected, index },
    },
  }) => {
    if (filters.length === 0) {
      setFilters([{ [name]: selected }]);
    } else if (filters.length > 0 && !filters[index]) {
      setFilters([...filters, { [name]: selected }]);
    } else
      setFilters((currentFilter) => [
        ...currentFilter.map((item, i) => {
          if (i == index) {
            return { ...item, [name]: selected };
          }
          return item;
        }),
      ]);
    setStep(step + 1);
    if (step === 3) {
      setIsOpen(false);
    }
    if (step === 2) inputRef.current?.focus();
  };

  useEffect(() => {
    const goFilter = filters.length === 0 || isFilterCompleted(filters.length - 1);

    if (goFilter) {
      onFilter(filters);
    }
  }, [filters]);

  return (
    <RelativeContainer>
      <TagsContainer flexDirection="row" gap="4px">
        {filters.map((filter, index) => (
          <Tag
            key={index}
            filter={filter}
            index={index}
            handleClearFilterItem={handleClearFilterItem}
          />
        ))}
        <RelativeContainer ref={searchRef}>
          <TextInput
            ref={inputRef}
            placeholder="search ..."
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            noShadow
            pr="40px"
            py="xxs"
          />
          {step === 1 && isOpen && (
            <DropDown index={current} name="criteria" onSelect={handleSelect} options={criterias} />
          )}
          {step === 2 && isOpen && (
            <DropDown index={current} name="operator" onSelect={handleSelect} options={operators} />
          )}
        </RelativeContainer>
      </TagsContainer>

      {filters.length > 0 ? (
        <ClearIcon top="15px" onClick={handleClear} />
      ) : (
        <SearchIcon animated={animate.current} />
      )}
    </RelativeContainer>
  );
}
